import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserRoleCode } from '../utils/userRoleUtils';
import { resolveHomePageForUser } from '../utils/loginRedirectUtils';
import { licenseService } from '../services/licenseService';
import { resolvePostLoginPath } from '../utils/postLoginNavigation';

export interface MenuItem {
  path: string;
  label: string;
  icon: string;
  submenu?: MenuItem[];
  roles?: string[];
}

export const useMenuPermissions = () => {
  const { user } = useAuth();

  const getUserRole = (): string => {
    return getUserRoleCode(user);
  };

  const isVisitor = (): boolean => {
    return getUserRole() === 'user';
  };

  const getAllowedMenus = (): MenuItem[] => {
    if (!user) return [];

    const userRole = getUserRoleCode(user);
    const isAdmin = userRole === 'admin';

    // Todos os menus disponíveis
    const allMenus: MenuItem[] = [
      {
        path: '/home',
        label: 'Início',
        icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
        roles: ['admin', 'manager', 'editor'],
      },
      /*{ 
        path: '/dashboard', 
        label: 'Dashboard', 
        icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
        roles: ['admin', 'manager', 'editor']
      },*/
      {
        path: '/sign/inbox',
        label: 'Minhas assinaturas',
        icon: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z',
        roles: ['user'],
      },
      {
        path: '/validate',
        label: 'Validar assinatura',
        icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
        roles: ['user'],
      },
      { 
        path: '/documents', 
        label: 'Documentos', 
        icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
        submenu: [
          { path: '/documents', label: 'Drive', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', roles: ['admin', 'manager', 'editor', 'viewer'] },
          { path: '/sign/inbox', label: 'Minhas assinaturas', icon: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z', roles: ['admin', 'manager', 'editor', 'viewer'] },
          { path: '/validate', label: 'Validar assinatura', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', roles: ['admin', 'manager', 'editor', 'viewer'] },
          { path: '/documents/favorites', label: 'Favoritos', icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z', roles: ['admin', 'manager', 'editor', 'viewer'] },
          { path: '/documents/private', label: 'Privados', icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z', roles: ['admin', 'manager', 'editor', 'viewer'] },
          { path: '/documents/categories', label: 'Categorias', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', roles: ['admin', 'manager', 'editor'] },
          { path: '/documents/tags', label: 'Tags', icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z', roles: ['admin', 'manager', 'editor'] },
          { path: '/documents/publish-queue', label: 'Solicitações de publicação', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01', roles: ['admin', 'editor'] },
        ],
        roles: ['admin', 'manager', 'editor', 'viewer']
      },
      { 
        path: '/copilot', 
        label: 'Copiloto', 
        icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
        roles: ['admin', 'manager', 'editor', 'viewer']
      },
      {
        path: '/forms',
        label: 'Formulários',
        icon: 'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z',
        roles: ['admin', 'manager', 'editor']
      },
      {
        path: '/settings/private-storage',
        label: 'Storage de usuários',
        icon: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4',
        roles: ['admin', 'manager']
      },
      { 
        path: '/templates', 
        label: 'Modelos', 
        icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
        roles: ['admin', 'manager', 'editor']
      },
      { 
        path: '/users', 
        label: 'Usuários', 
        icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
        roles: ['admin', 'manager']
      },
      { 
        path: '/teams', 
        label: 'Equipes', 
        icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
        roles: ['admin', 'manager']
      },
      { 
        path: '/profiles', 
        label: 'Perfis', 
        icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
        roles: ['admin', 'manager']
      },
      { 
        path: '/permissions', 
        label: 'Permissões', 
        icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z',
        roles: ['admin', 'manager']
      },
      { 
        path: '/workflows', 
        label: 'Workflows', 
        icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
        roles: ['admin', 'manager']
      },
      { 
        path: '/notifications', 
        label: 'Notificações', 
        icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9'
        // Removido roles para permitir acesso universal
      },
      { 
        path: '/settings', 
        label: 'Configurações', 
        icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
        roles: ['admin']
      },
      { 
        path: '/audit', 
        label: 'Auditoria', 
        icon: 'M9 17v-2a4 4 0 014-4h3m4 0a9 9 0 11-18 0 9 9 0 0118 0z',
        roles: ['admin', 'manager', 'editor']
      },
      {
        path: '/jobs',
        label: 'Processamentos (Jobs)',
        icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
        roles: ['admin', 'manager', 'editor', 'viewer']
      },
    ];

    // Filtrar menus baseado na role do usuário
    const filteredMenus = allMenus.filter(menu => {
      if (menu.path === '/sign/inbox' || menu.path === '/validate') {
        return userRole === 'user';
      }

      // Admin pode ver tudo
      if (isAdmin) return true;
      
      // Se o menu não tem roles definidas, permitir acesso (como Notificações)
      if (!menu.roles || menu.roles.length === 0) return true;
      
      // Verificar se o usuário tem uma das roles permitidas
      return menu.roles.includes(userRole);
    });

    // Filtrar submenus baseado na role do usuário
    return filteredMenus.map(menu => {
      if (menu.submenu) {
        const filteredSubmenu = menu.submenu.filter(subItem => {
          // Se o submenu não tem roles definidas, permitir acesso
          if (!subItem.roles || subItem.roles.length === 0) return true;
          
          // Admin pode ver todos os submenus
          if (isAdmin) return true;
          
          // Verificar se o usuário tem uma das roles permitidas
          return subItem.roles.includes(userRole);
        });

        return {
          ...menu,
          submenu: filteredSubmenu
        };
      }
      
      return menu;
    })
      .filter((menu) => {
        if (menu.submenu && menu.submenu.length === 0) {
          return false;
        }
        return true;
      });
  };

  const hasPermission = (requiredRoles: string[]): boolean => {
    if (!user) {
      return false;
    }

    const userRole = getUserRoleCode(user);

    if (userRole === 'admin') {
      return true;
    }

    const normalizedRequiredRoles = requiredRoles.map((role) => role.toLowerCase());
    return normalizedRequiredRoles.includes(userRole);
  };

  const isAdmin = (): boolean => {
    return getUserRoleCode(user) === 'admin';
  };

  const getHomePage = useCallback((): string => {
    if (!user) {
      return '/login';
    }

    const license = licenseService.getCachedLicense();
    if (license) {
      return resolvePostLoginPath(
        license,
        {
          isAdmin: user.isAdmin,
          roleCode: user.role?.code,
        },
        null
      );
    }

    return resolveHomePageForUser({
      isAdmin: user.isAdmin,
      roleCode: user.role?.code,
    });
  }, [user?.id, user?.role?.code, user?.isAdmin]);

  return {
    getAllowedMenus,
    hasPermission,
    getUserRole,
    isVisitor,
    isAdmin,
    getHomePage,
    user
  };
}; 